import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Card, Checkbox, Input, Table, Typography } from 'antd'
import moment from 'moment'
import PropTypes from 'prop-types'

const { Link, Paragraph, Text } = Typography
const { Search } = Input

const PAGE_SIZE = 25
const PAGINATION_THRESHOLD = 20

const buildEdpPath = (slug) => {
  if (!slug || typeof slug !== 'string') {
    return ''
  }
  const normalized = slug.startsWith('/') ? slug : `/${slug}`
  return `/events-tickets${normalized}`
}

const rowMatchesSearch = (row, queryLower) => {
  if (!queryLower) return true
  const name = String(row.name || '').toLowerCase()
  const slug = String(row.slug || '').toLowerCase()
  const id = String(row.id || '').toLowerCase()
  const url = String(row.msgEdpUrl || row.msg_edp_url || '').toLowerCase()
  const path = buildEdpPath(row.slug).toLowerCase()
  return (
    name.includes(queryLower) ||
    slug.includes(queryLower) ||
    id.includes(queryLower) ||
    url.includes(queryLower) ||
    path.includes(queryLower)
  )
}

const PriorSameArtistEdpRedirects = ({
  currentSlug,
  disabled,
  edpRedirectPermanent,
  priorRows,
  publish,
  selectedPriorEdpIds,
  setEdpRedirectPermanent,
  setSelectedPriorEdpIds
}) => {
  const [searchText, setSearchText] = useState('')
  const [page, setPage] = useState(1)

  const destination = useMemo(() => buildEdpPath(currentSlug), [currentSlug])

  const dataSource = useMemo(() => (priorRows || []).map((row) => ({
    ...row,
    key: row.id
  })), [priorRows])

  const queryLower = useMemo(() => searchText.trim().toLowerCase(), [searchText])

  const filteredRows = useMemo(() => {
    if (!queryLower) return dataSource
    return dataSource.filter((row) => rowMatchesSearch(row, queryLower))
  }, [dataSource, queryLower])

  useEffect(() => {
    setPage(1)
    setSearchText('')
  }, [priorRows])

  useEffect(() => {
    if (filteredRows.length <= PAGINATION_THRESHOLD) return
    const maxPage = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE))
    if (page > maxPage) {
      setPage(maxPage)
    }
  }, [filteredRows.length, page])

  const usePagination = filteredRows.length > PAGINATION_THRESHOLD

  const tableDataSource = useMemo(() => {
    if (!usePagination) return filteredRows
    const start = (page - 1) * PAGE_SIZE
    return filteredRows.slice(start, start + PAGE_SIZE)
  }, [filteredRows, page, usePagination])

  const columns = useMemo(() => ([
    {
      title: 'Event',
      dataIndex: 'name',
      ellipsis: true,
      key: 'name'
    },
    {
      key: 'start',
      render: (_, row) => {
        const d = row.startDate || row.start_date
        return d ? moment.utc(d).format('MM/DD/YYYY') : '—'
      },
      title: 'Start date',
      width: '15%'
    },
    {
      ellipsis: true,
      key: 'source',
      render: (_, row) => {
        const s = row.slug
        return buildEdpPath(s) || '—'
      },
      title: 'Redirect source (EDP path)'
    }
  ]), [])

  const pagination = useMemo(() => {
    if (filteredRows.length <= PAGINATION_THRESHOLD) return false
    return {
      current: page,
      onChange: (p) => setPage(p),
      pageSize: PAGE_SIZE,
      showSizeChanger: false,
      showTotal: (total) => `${total} prior EDP(s)`,
      total: filteredRows.length
    }
  }, [filteredRows.length, page])

  if (!priorRows || priorRows.length === 0) {
    return null
  }

  const rowSelection = {
    getCheckboxProps: () => ({ disabled }),
    onChange: (keys) => setSelectedPriorEdpIds(keys),
    selectedRowKeys: selectedPriorEdpIds
  }

  const onSearchChange = (e) => {
    setSearchText(e.target.value)
    setPage(1)
  }

  return (
    <Card style={{ marginTop: 16 }} title="Redirects from prior artist EDPs">
      <Paragraph type="secondary">
        These published events share the same artist (Ticketmaster ID or name). Select any you want to redirect to the new EDP URL for this page when you publish.
        {' '}
        <Link href="/redirects" rel="noreferrer" target="_blank">Manage all redirects</Link>
      </Paragraph>
      {!publish && selectedPriorEdpIds.length > 0 && (
        <Alert
          message="Turn on Publish and click Update to create the selected redirects when this event is first published."
          showIcon
          style={{ marginBottom: 12 }}
          type="info"
        />
      )}
      <Search
        allowClear
        disabled={disabled}
        onChange={onSearchChange}
        placeholder="Search by event name, Ticketmaster ID, slug, or URL"
        style={{ marginBottom: 12 }}
        value={searchText}
      />
      <Table
        columns={columns}
        dataSource={tableDataSource}
        locale={{ emptyText: queryLower ? 'No prior EDPs match your search.' : 'No prior EDPs.' }}
        pagination={pagination}
        rowSelection={rowSelection}
        size="small"
      />
      <div style={{ marginTop: 12 }}>
        <Text strong>New EDP destination: </Text>
        <Text code>{destination || '(set a slug to preview path)'}</Text>
      </div>
      <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 12 }}>
        <Checkbox
          checked={edpRedirectPermanent}
          disabled={disabled}
          onChange={(e) => setEdpRedirectPermanent(e.target.checked)}
        >
          Use permanent (301) redirects
        </Checkbox>
      </div>
    </Card>
  )
}

PriorSameArtistEdpRedirects.propTypes = {
  currentSlug: PropTypes.string,
  disabled: PropTypes.bool,
  edpRedirectPermanent: PropTypes.bool,
  priorRows: PropTypes.array,
  publish: PropTypes.bool,
  selectedPriorEdpIds: PropTypes.arrayOf(PropTypes.string),
  setEdpRedirectPermanent: PropTypes.func.isRequired,
  setSelectedPriorEdpIds: PropTypes.func.isRequired
}

export default PriorSameArtistEdpRedirects
